import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Power, KeyRound, Trash2, QrCode, Copy } from "lucide-react";
import { QRCodeImage, downloadQR } from "@/components/QRCodeImage";
import { ImageUpload } from "@/components/ImageUpload";

const empty = {
  name: "", logo_url: "", address: "", contact_number: "",
  whatsapp_number: "", google_review_link: "",
  login_id: "", password: "",
  account_manager_name: "", account_manager_contact: "",
  plan: "monthly",
};

export const AdminRestaurants = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const load = () => api.get("/admin/restaurants").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/restaurants", form);
      toast.success("Restaurant created");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const suspend = async (r) => {
    try {
      await api.post(`/admin/restaurants/${r.id}/${r.suspended ? "activate" : "suspend"}`);
      toast.success(r.suspended ? "Activated" : "Suspended");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.name}? This is permanent.`)) return;
    try {
      await api.delete(`/admin/restaurants/${r.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const resetPassword = async () => {
    try {
      await api.post(`/admin/restaurants/${resetFor.id}/reset-password`, { new_password: resetPwd });
      toast.success("Password reset");
      setResetFor(null); setResetPwd("");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const menuUrl = (slug) => `${window.location.origin}/m/${slug}`;
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div data-testid="admin-restaurants">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Restaurants</h1>
          <p className="text-sm text-zq-muted mt-1">Create accounts, manage suspensions & QR codes.</p>
        </div>
        <button className="zq-btn zq-btn-primary" onClick={() => setOpen(true)} data-testid="create-restaurant-btn">
          <Plus size={18}/> New restaurant
        </button>
      </div>

      <div className="zq-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zq-orange-light text-left">
            <tr>
              <th className="p-3">Restaurant</th>
              <th className="p-3">Login ID</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Status</th>
              <th className="p-3">Menu URL</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zq-muted">No restaurants yet. Create your first.</td></tr>
            )}
            {list.map((r) => (
              <tr key={r.id} className="border-t border-zq-border">
                <td className="p-3">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-zq-muted">{r.address}</div>
                </td>
                <td className="p-3 font-mono text-xs">—</td>
                <td className="p-3 capitalize">{r.plan}</td>
                <td className="p-3">
                  <span className={`zq-pill ${r.suspended ? "zq-pill-gray" : "zq-pill-green"}`}>
                    {r.suspended ? "Suspended" : "Active"}
                  </span>
                </td>
                <td className="p-3">
                  <button className="text-xs text-zq-orange flex items-center gap-1" onClick={() => copy(menuUrl(r.slug))} data-testid={`copy-url-${r.slug}`}>
                    <Copy size={12}/> /m/{r.slug}
                  </button>
                </td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <button className="zq-btn zq-btn-ghost !px-2" title="QR" onClick={() => setQrFor(r)} data-testid={`qr-${r.slug}`}><QrCode size={16}/></button>
                  <button className="zq-btn zq-btn-ghost !px-2" title="Reset password" onClick={() => setResetFor(r)} data-testid={`reset-${r.slug}`}><KeyRound size={16}/></button>
                  <button className="zq-btn zq-btn-ghost !px-2" title={r.suspended ? "Activate" : "Suspend"} onClick={() => suspend(r)} data-testid={`suspend-${r.slug}`}><Power size={16}/></button>
                  <button className="zq-btn zq-btn-ghost !px-2 text-red-600" title="Delete" onClick={() => del(r)} data-testid={`delete-${r.slug}`}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-restaurant-modal">
          <div className="zq-card w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-bold">New restaurant</h3>
              <button onClick={() => setOpen(false)}><X size={18}/></button>
            </div>
            <form className="mt-5 grid sm:grid-cols-2 gap-3" onSubmit={create}>
              <div className="sm:col-span-2">
                <ImageUpload
                  value={form.logo_url}
                  onChange={(url) => setForm({ ...form, logo_url: url })}
                  folder="zaykaqr/uploads/logos"
                  label="Restaurant Logo"
                  aspect="aspect-[3/1]"
                  testId="new-logo-upload"
                />
              </div>
              {[
                ["name", "Restaurant Name", true],
                ["whatsapp_number", "WhatsApp Number", true],
                ["address", "Address"],
                ["contact_number", "Contact Number"],
                ["google_review_link", "Google Review Link"],
                ["login_id", "Login ID", true],
                ["password", "Password", true, "password"],
                ["account_manager_name", "Account Manager Name"],
                ["account_manager_contact", "Account Manager Contact"],
              ].map(([k, l, req, type]) => (
                <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-medium text-zq-muted">{l}{req && " *"}</label>
                  <input
                    className="zq-input"
                    required={!!req}
                    type={type || "text"}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`new-${k}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-zq-muted">Plan</label>
                <select className="zq-input" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="new-plan">
                  <option value="monthly">Monthly (₹1,299)</option>
                  <option value="quarterly">Quarterly (₹3,000)</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex gap-2 mt-2">
                <button type="button" className="zq-btn zq-btn-outline flex-1" onClick={() => setOpen(false)}>Cancel</button>
                <button className="zq-btn zq-btn-primary flex-1" disabled={busy} data-testid="new-submit">
                  {busy ? "Creating…" : "Create restaurant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="zq-card max-w-sm w-full p-6 text-center">
            <button className="absolute m-2 top-3 right-3" onClick={() => setQrFor(null)}><X size={18}/></button>
            <h3 className="font-bold text-lg">{qrFor.name}</h3>
            <p className="text-xs text-zq-muted mt-1">{menuUrl(qrFor.slug)}</p>
            <div className="mt-4 flex justify-center">
              <QRCodeImage data={menuUrl(qrFor.slug)} size={240} />
            </div>
            <button
              className="zq-btn zq-btn-primary w-full mt-5"
              onClick={() => downloadQR(menuUrl(qrFor.slug), `zaykaqr-${qrFor.slug}.png`)}
              data-testid="download-qr-btn"
            >
              Download QR (PNG)
            </button>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="zq-card max-w-sm w-full p-6">
            <button className="absolute m-2 top-3 right-3" onClick={() => setResetFor(null)}><X size={18}/></button>
            <h3 className="font-bold text-lg">Reset password</h3>
            <p className="text-sm text-zq-muted mt-1">For {resetFor.name}</p>
            <input className="zq-input mt-4" placeholder="New password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-password-input" />
            <button className="zq-btn zq-btn-primary w-full mt-4" disabled={!resetPwd} onClick={resetPassword} data-testid="reset-password-submit">Reset</button>
          </div>
        </div>
      )}
    </div>
  );
};
